import React, { FC, useState } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Stack from "../stack/Stack";
import { Cross, Info, SquarePlus, Tick } from "../icon";
import Typography from "../typography/Typography";
import Html from "../html/Html";
import Divider from "../divider/Divider";
import { LinkType } from "../../types";
import Link from "../link/Link";

// Interface

export interface DetailsProps {
  items: DetailItem[];
  tickColor?: "green" | "darkBlue";
  heading?: string;
  callToAction?: LinkType;
}

export interface DetailItem {
  heading: string;
  detail: string;
  included?: boolean;
  extraDetail?: string;
}

export type DetailItemProps = DetailItem & {
  tickColor?: "green" | "darkBlue";
};

// Styles

const StyledDetails = styled(Stack)`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    li + li {
      margin-top: ${space.fluidXxSmallToxSmall};
      padding-top: ${space.fluidXxSmallToxSmall};
      border-top: solid 1px currentColor;
    }
  `};
`;

const StyledHeading = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    display: flex;
    gap: ${space.xxSmall};
    justify-content: space-between;
  `};
`;

const StyledIconHeading = styled.span`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    display: flex;
    gap: ${space.xxSmall};
  `};
`;

// JSX

export const Detail: FC<DetailItemProps> = ({
  heading,
  detail,
  extraDetail,
  included,
  tickColor,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Stack gap="fluidXxxxSmallToXxxSmall" as="li">
      <StyledHeading>
        <StyledIconHeading>
          {included !== undefined ? (
            included ? (
              <Tick color={tickColor} />
            ) : (
              <SquarePlus color="lilac" />
            )
          ) : null}
          {heading && (
            <Typography component="h3" variant="bodyBold">
              {heading}
            </Typography>
          )}
          {!heading && detail ? <Html>{detail}</Html> : ""}
        </StyledIconHeading>
        {extraDetail && (
          <button type="button" title="Learn more" onClick={handleOpen}>
            <Info size="small" />
          </button>
        )}
      </StyledHeading>
      {extraDetail && isOpen ? <Html>{extraDetail}</Html> : ""}
      {detail && heading ? <Html>{detail}</Html> : ""}
    </Stack>
  );
};

export const Details: FC<DetailsProps> = ({
  items,
  heading,
  tickColor = "green",
  callToAction,
  ...rest
}) => (
  <Stack gap="medium" {...rest}>
    <StyledDetails gap="fluidXxSmallToxSmall" >
      {heading && (
        <Stack gap="fluidXxSmallToxSmall">
          <Typography component="h3" variant="bodyBold">
            {heading}
          </Typography>
          <Divider />
        </Stack>
      )}
      <ul>
        {items?.map((item, index) => (
          <Detail key={index} {...item} tickColor={tickColor} />
        ))}
      </ul>
    </StyledDetails>
    {callToAction && <Link {...callToAction} />}
  </Stack>
);

export default Details;
